import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../../config';
import { useOrganization } from '../../../context/OrganizationContext';

import {
  ArrowSquareOut,
  Bell,
  Calendar,
  CaretDown,
  CaretRight,
  ChatText,
  CheckCircle,
  Clock,
  Code,
  Cpu,
  Database,
  Envelope,
  Eye,
  EyeSlash,
  FileText,
  GearSix,
  Globe,
  HardDrives,
  Image,
  Info,
  Key,
  Lightning,
  LinkSimple,
  Lock,
  LockOpen,
  Microphone,
  Pause,
  PencilSimple,
  Play,
  Plus,
  Trash,
  UploadSimple,
  Warning,
  WarningCircle,
  WebhooksLogo,
  XCircle
} from '@phosphor-icons/react';
/**
 * ConfigPanel - Consolidated configuration tab for Unified Agent Builder
 * 
 * Per consolidation spec, this is the canonical home for all advanced features:
 * - Section 1: Agent Identity
 * - Section 2: Inputs & Triggers
 * - Section 3: Capabilities & Bindings
 * - Section 4: Knowledge Base (persistent files)
 * - Section 5: Integrations & MCP Bindings (NEW)
 * - Section 6: Agent-Scoped Automations (NEW)
 * - Section 7: Advanced Settings (collapsed by default)
 */
const ConfigPanel = ({
  agentConfig,
  onConfigUpdate,
  capabilitiesLocked = false,
  sourceData = null,
  agentId = null,
  onSave = null,
  hasUnsavedChanges = false
}) => {
  const { activeOrganization } = useOrganization();
  const [expandedSections, setExpandedSections] = useState(['identity', 'inputs', 'capabilities']);
  const [showAdvancedWarning, setShowAdvancedWarning] = useState(false);
  
  // Navigation warning state
  const [navigationWarning, setNavigationWarning] = useState({
    show: false,
    targetUrl: '',
    targetLabel: ''
  });
  
  // Integrations state
  const [connections, setConnections] = useState([]);
  const [mcpProviders, setMcpProviders] = useState([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  
  // Automations state
  const [automations, setAutomations] = useState([]);
  const [loadingAutomations, setLoadingAutomations] = useState(false);
  const [showAddAutomation, setShowAddAutomation] = useState(false);
  const [newAutomation, setNewAutomation] = useState({
    name: '',
    trigger: 'session.completed',
    action_type: 'webhook.post',
    action_config: {},
    is_active: true
  });

  // Handle external link click with warning
  const handleExternalLinkClick = (url, label) => {
    if (hasUnsavedChanges) {
      setNavigationWarning({ show: true, targetUrl: url, targetLabel: label });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Handle navigation after warning
  const handleNavigationConfirm = async (saveFirst) => {
    if (saveFirst && onSave) {
      await onSave(false); // Save as draft
      toast.success('Draft saved');
    }
    window.open(navigationWarning.targetUrl, '_blank', 'noopener,noreferrer');
    setNavigationWarning({ show: false, targetUrl: '', targetLabel: '' });
  };

  // AI Model state
  const [showApiKey, setShowApiKey] = useState(false);

  // AI Model providers and their models
  const aiProviders = [
    { 
      id: 'openai', 
      label: 'OpenAI', 
      models: [
        { id: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
        { id: 'gpt-4', label: 'GPT-4' },
        { id: 'gpt-4o', label: 'GPT-4o' },
        { id: 'gpt-4o-mini', label: 'GPT-4o Mini' },
        { id: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }
      ]
    },
    { 
      id: 'anthropic', 
      label: 'Claude (Anthropic)', 
      models: [
        { id: 'claude-3-opus', label: 'Claude 3 Opus' },
        { id: 'claude-3-sonnet', label: 'Claude 3 Sonnet' },
        { id: 'claude-3-haiku', label: 'Claude 3 Haiku' },
        { id: 'claude-3.5-sonnet', label: 'Claude 3.5 Sonnet' }
      ]
    },
    { 
      id: 'google', 
      label: 'Gemini (Google)', 
      models: [
        { id: 'gemini-pro', label: 'Gemini Pro' },
        { id: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
        { id: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' }
      ]
    },
    { 
      id: 'mistral', 
      label: 'Mistral AI', 
      models: [
        { id: 'mistral-large', label: 'Mistral Large' },
        { id: 'mistral-medium', label: 'Mistral Medium' },
        { id: 'mistral-small', label: 'Mistral Small' },
        { id: 'mixtral-8x7b', label: 'Mixtral 8x7B' },
        { id: 'mistral-7b', label: 'Mistral 7B' }
      ]
    }
  ];

  const getModelsForProvider = (providerId) => {
    const provider = aiProviders.find(p => p.id === providerId);
    return provider?.models || [];
  };

  const toggleSection = (section) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Load integrations and automations
  useEffect(() => {
    if (activeOrganization?.organization_id) {
      loadIntegrations();
      if (agentId) {
        loadAutomations();
      }
    }
  }, [activeOrganization?.organization_id, agentId]);

  const loadIntegrations = async () => {
    setLoadingIntegrations(true);
    try {
      // Load OAuth connections
      const connRes = await fetch(`${BACKEND_URL}/api/automation/connections`, {
        credentials: 'include',
        headers: { 'X-Organization-Id': activeOrganization.organization_id }
      });
      if (connRes.ok) {
        const data = await connRes.json();
        setConnections(data.connections || []);
      }
      
      // Load MCP providers (from settings)
      const mcpRes = await fetch(`${BACKEND_URL}/api/mcp/providers`, {
        credentials: 'include',
        headers: { 'X-Organization-Id': activeOrganization.organization_id }
      });
      if (mcpRes.ok) {
        const data = await mcpRes.json();
        setMcpProviders(data.providers || []);
      }
    } catch (err) {
      console.error('Error loading integrations:', err);
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const loadAutomations = async () => {
    if (!agentId) return;
    setLoadingAutomations(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/automation/rules?agent_id=${agentId}`, {
        credentials: 'include',
        headers: { 'X-Organization-Id': activeOrganization.organization_id }
      });
      if (res.ok) {
        const data = await res.json();
        setAutomations(data.rules || []);
      }
    } catch (err) {
      console.error('Error loading automations:', err);
    } finally {
      setLoadingAutomations(false);
    }
  };

  // Input type options
  const inputTypes = [
    { id: 'text', label: 'Text Input', icon: ChatText, description: 'Accept text messages' },
    { id: 'file_pdf', label: 'PDF Files', icon: FileText, description: 'Accept PDF documents' },
    { id: 'file_doc', label: 'Documents', icon: FileText, description: 'Accept Word/text files' },
    { id: 'audio', label: 'Audio/Voice', icon: Microphone, description: 'Accept audio input' },
    { id: 'image', label: 'Images', icon: Image, description: 'Accept image files' },
    { id: 'webhook', label: 'WebhooksLogo', icon: WebhooksLogo, description: 'Receive webhook payloads' }
  ];

  // Trigger options
  const triggerTypes = [
    { id: 'manual', label: 'Manual', description: 'Triggered by user action' },
    { id: 'schedule', label: 'Schedule', description: 'Run on a schedule (cron)' },
    { id: 'event', label: 'Event', description: 'Triggered by system event' },
    { id: 'webhook', label: 'WebhooksLogo', description: 'Triggered by incoming webhook' }
  ];

  // Automation triggers
  const automationTriggers = [
    { id: 'session.completed', label: 'Session Completed', icon: CheckCircle },
    { id: 'capability.output', label: 'Capability Output', icon: Lightning },
    { id: 'error.occurred', label: 'Error Occurred', icon: WarningCircle }
  ];

  // Automation actions
  const automationActions = [
    { id: 'webhook.post', label: 'WebhooksLogo POST', icon: Globe },
    { id: 'email.send', label: 'Send Email', icon: Envelope },
    { id: 'calendar.create', label: 'Create Calendar Event', icon: Calendar }
  ];

  // Provider options for capability binding
  const providerOptions = [
    { id: 'internal', label: 'Internal', description: 'Built-in capability' },
    { id: 'oauth', label: 'OAuth Provider', description: 'Connected via OAuth' },
    { id: 'mcp', label: 'MCP Provider', description: 'External MCP server' }
  ];

  const handleInputToggle = (inputId) => {
    const current = agentConfig.accepted_inputs || [];
    const updated = current.includes(inputId)
      ? current.filter(i => i !== inputId)
      : [...current, inputId];
    onConfigUpdate({ accepted_inputs: updated });
  };

  const handleCapabilityRemove = (capability) => {
    if (capabilitiesLocked) {
      toast.error('Capabilities are locked for this opportunity-based agent');
      return;
    }
    const updated = (agentConfig.required_capabilities || []).filter(c => c !== capability);
    onConfigUpdate({ required_capabilities: updated });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // For now, just add to local state - backend integration needed
    const newFiles = files.map(f => ({
      id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: f.name,
      size: f.size,
      type: f.type,
      uploadedAt: new Date().toISOString()
    }));

    const currentKB = agentConfig.knowledge_base || [];
    onConfigUpdate({ knowledge_base: [...currentKB, ...newFiles] });
    toast.success(`${files.length} file(s) added to knowledge base`);
  };

  const handleFileRemove = (fileId) => {
    const updated = (agentConfig.knowledge_base || []).filter(f => f.id !== fileId);
    onConfigUpdate({ knowledge_base: updated });
  };

  const handleAddAutomation = async () => {
    if (!agentId) {
      toast.error('Save the agent first to add automations');
      return;
    }
    if (!newAutomation.name.trim()) {
      toast.error('Automation name is required');
      return;
    }
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/automation/rules`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': activeOrganization.organization_id
        },
        body: JSON.stringify({
          ...newAutomation,
          agent_id: agentId
        })
      });
      
      if (res.ok) {
        toast.success('Automation added');
        setShowAddAutomation(false);
        setNewAutomation({
          name: '',
          trigger: 'session.completed',
          action_type: 'webhook.post',
          action_config: {},
          is_active: true
        });
        loadAutomations();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add automation');
      }
    } catch (err) {
      toast.error('Failed to add automation');
    }
  };

  const handleToggleAutomation = async (automation) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/automation/rules/${automation.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': activeOrganization.organization_id
        },
        body: JSON.stringify({ is_active: !automation.is_active })
      });
      
      if (res.ok) {
        loadAutomations();
      }
    } catch (err) {
      toast.error('Failed to update automation');
    }
  };

  const handleDeleteAutomation = async (automationId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/automation/rules/${automationId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Organization-Id': activeOrganization.organization_id }
      });
      
      if (res.ok) {
        toast.success('Automation deleted');
        loadAutomations();
      }
    } catch (err) {
      toast.error('Failed to delete automation');
    }
  };

  const SectionHeader = ({ id, title, icon: Icon, count, locked, badge }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-white/5">
          <Icon size={16} className="text-indigo-400" />
        </div>
        <span className="text-sm font-medium text-white">{title}</span>
        {count !== undefined && (
          <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-slate-400">
            {count}
          </span>
        )}
        {badge && (
          <span className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-500/20 text-indigo-400 uppercase">
            {badge}
          </span>
        )}
        {locked && (
          <Lock size={12} className="text-amber-400" />
        )}
      </div>
      <CaretRight 
        size={16} 
        className={`text-slate-500 transition-transform ${
          expandedSections.includes(id) ? 'rotate-90' : ''
        }`}
      />
    </button>
  );

  return (
    <div className="h-full overflow-y-auto" data-testid="config-panel">
      {/* Section 1: Agent Identity */}
      <div id="config-section-identity" className="border-b border-white/10 transition-all duration-300">
        <SectionHeader id="identity" title="Agent Identity" icon={GearSix} />
        <AnimatePresence>
          {expandedSections.includes('identity') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Agent Name *</label>
                  <input
                    type="text"
                    value={agentConfig.name || ''}
                    onChange={(e) => onConfigUpdate({ name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                      text-sm text-white focus:outline-none focus:border-indigo-500/50"
                    placeholder="My Agent"
                    data-testid="config-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Description</label>
                  <textarea
                    value={agentConfig.description || ''}
                    onChange={(e) => onConfigUpdate({ description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                      text-sm text-white focus:outline-none focus:border-indigo-500/50 resize-none"
                    placeholder="What does this agent do?"
                    data-testid="config-description-input"
                  />
                </div>
                
                {/* Status (Read-only) */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Status</label>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                      agentConfig.status === 'active' 
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    }`}>
                      {agentConfig.status === 'active' ? 'Active' : 'Draft'}
                    </span>
                    <span className="text-xs text-slate-500">
                      {agentConfig.status === 'active' 
                        ? 'Agent is live and processing requests'
                        : 'Deploy from Agent Details to activate'}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Section 2: Inputs & Triggers (Read-only Summary) */}
      <div id="config-section-inputs" className="border-b border-white/10 transition-all duration-300">
        <SectionHeader 
          id="inputs" 
          title="Inputs & Triggers" 
          icon={Lightning}
          count={(agentConfig.accepted_inputs || []).length}
        />
        <AnimatePresence>
          {expandedSections.includes('inputs') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                {/* Info Banner */}
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <Info size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-300">
                    Configure inputs and triggers on individual nodes in the Visual Flow tab.
                  </p>
                </div>

                {/* Read-only Input Summary */}
                <div>
                  <label className="block text-xs text-slate-400 mb-2">Active Inputs</label>
                  <div className="flex flex-wrap gap-2">
                    {(agentConfig.accepted_inputs || ['text']).map(input => {
                      const inputDef = inputTypes.find(i => i.id === input);
                      const InputIcon = inputDef?.icon || ChatText;
                      return (
                        <div
                          key={input}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg 
                            bg-indigo-500/10 border border-indigo-500/20 text-indigo-300"
                        >
                          <InputIcon size={12} />
                          <span className="text-xs">{inputDef?.label || input}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Trigger Type Summary */}
                <div>
                  <label className="block text-xs text-slate-400 mb-2">Trigger Type</label>
                  <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                    <span className="text-sm text-white">
                      {triggerTypes.find(t => t.id === (agentConfig.trigger_type || 'manual'))?.label || 'Manual'}
                    </span>
                    <p className="text-xs text-slate-500 mt-1">
                      {triggerTypes.find(t => t.id === (agentConfig.trigger_type || 'manual'))?.description || ''}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Section 3: Capabilities & Bindings */}
      <div id="config-section-capabilities" className="border-b border-white/10 transition-all duration-300">
        <SectionHeader 
          id="capabilities" 
          title="Capabilities" 
          icon={Lightning}
          count={(agentConfig.required_capabilities || []).length}
          locked={capabilitiesLocked}
        />
        <AnimatePresence>
          {expandedSections.includes('capabilities') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                {capabilitiesLocked && (
                  <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <Lock size={14} className="text-amber-400 mt-0.5" />
                    <p className="text-xs text-amber-300">
                      Capabilities are locked for opportunity-based agents.
                    </p>
                  </div>
                )}
                
                {(agentConfig.required_capabilities || []).length > 0 ? (
                  <div className="space-y-2">
                    {(agentConfig.required_capabilities || []).map(cap => (
                      <div
                        key={cap}
                        className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10"
                      >
                        <div className="flex items-center gap-2">
                          <Lightning size={14} className="text-indigo-400" />
                          <span className="text-sm text-white">{cap}</span>
                        </div>
                        {!capabilitiesLocked && (
                          <button
                            onClick={() => handleCapabilityRemove(cap)}
                            className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 rounded-lg border border-dashed border-white/10 text-center">
                    <p className="text-xs text-slate-500">No capabilities configured</p>
                    <p className="text-[10px] text-slate-600 mt-1">
                      Add capabilities in the Visual Flow tab
                    </p>
                  </div>
                )}

                {/* Capability Bindings */}
                {(agentConfig.required_capabilities || []).length > 0 && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-2">Provider Bindings</label>
                    <div className="space-y-2">
                      {(agentConfig.required_capabilities || []).map(cap => (
                        <div key={cap} className="flex items-center gap-2">
                          <span className="text-xs text-slate-400 w-24 truncate">{cap}</span>
                          <select
                            value={(agentConfig.capability_bindings || {})[cap] || 'internal'}
                            onChange={(e) => onConfigUpdate({
                              capability_bindings: {
                                ...agentConfig.capability_bindings,
                                [cap]: e.target.value
                              }
                            })}
                            className="flex-1 px-2 py-1.5 rounded bg-white/5 border border-white/10 
                              text-xs text-white focus:outline-none focus:border-indigo-500/50"
                          >
                            {providerOptions.map(opt => (
                              <option key={opt.id} value={opt.id}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Section 4: Knowledge Base */}
      <div className="border-b border-white/10">
        <SectionHeader 
          id="knowledge" 
          title="Knowledge Base" 
          icon={Database}
          count={(agentConfig.knowledge_base || []).length}
        />
        <AnimatePresence>
          {expandedSections.includes('knowledge') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                <div className="p-3 rounded-lg border border-dashed border-white/10 bg-white/[0.02]">
                  <label className="cursor-pointer block text-center">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.md"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <UploadSimple size={20} className="mx-auto text-slate-500 mb-2" />
                    <p className="text-xs text-slate-400">Drop files or click to upload</p>
                  </label>
                  <p className="text-[10px] text-slate-500 mt-1 text-center">
                    Supported: PDF, DOC, DOCX, TXT, MD
                  </p>
                </div>

                {/* File List */}
                {(agentConfig.knowledge_base || []).length > 0 && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-2">Indexed Files</label>
                    <div className="space-y-2">
                      {(agentConfig.knowledge_base || []).map(file => (
                        <div 
                          key={file.id}
                          className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10"
                        >
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="text-emerald-400" />
                            <span className="text-sm text-white truncate max-w-[200px]">{file.name}</span>
                            <span className="text-[10px] text-slate-500">
                              {(file.size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                          <button
                            onClick={() => handleFileRemove(file.id)}
                            className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Section 5: Integrations & MCP Bindings */}
      <div className="border-b border-white/10">
        <SectionHeader 
          id="integrations" 
          title="Integrations & MCP" 
          icon={LinkSimple}
          count={connections.length + mcpProviders.length}
        />
        <AnimatePresence>
          {expandedSections.includes('integrations') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                {loadingIntegrations ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* OAuth Connections */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">OAuth Connections</label>
                      {connections.length > 0 ? (
                        <div className="space-y-2">
                          {connections.map(conn => (
                            <div 
                              key={conn.id}
                              className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10"
                            >
                              <div className="flex items-center gap-2">
                                <Globe size={14} className="text-blue-400" />
                                <span className="text-sm text-white">{conn.provider_name || conn.provider}</span>
                                {conn.is_active && (
                                  <CheckCircle size={12} className="text-emerald-400" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 p-2">No OAuth connections configured</p>
                      )}
                      <button 
                        onClick={() => handleExternalLinkClick('/settings/automation-integrations', 'Manage Connections')}
                        className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 mt-2"
                      >
                        <Plus size={12} />
                        Manage Connections
                        <ArrowSquareOut size={10} />
                      </button>
                    </div>

                    {/* MCP Providers */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">MCP Providers</label>
                      {mcpProviders.length > 0 ? (
                        <div className="space-y-2">
                          {mcpProviders.map(provider => (
                            <div 
                              key={provider.id}
                              className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10"
                            >
                              <div className="flex items-center gap-2">
                                <HardDrives size={14} className="text-purple-400" />
                                <span className="text-sm text-white">{provider.name}</span>
                                {provider.status === 'connected' && (
                                  <CheckCircle size={12} className="text-emerald-400" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 p-2">No MCP providers configured</p>
                      )}
                      <button 
                        onClick={() => handleExternalLinkClick('/settings/mcp', 'Configure MCP')}
                        className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 mt-2"
                      >
                        <Plus size={12} />
                        Configure MCP
                        <ArrowSquareOut size={10} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Section 6: Agent-Specific AI Model */}
      <div className="border-b border-white/10">
        <SectionHeader 
          id="ai-model" 
          title="AI Model Override" 
          icon={Cpu}
        />
        <AnimatePresence>
          {expandedSections.includes('ai-model') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                {/* Toggle Switch */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white font-medium">Use Custom AI Model</p>
                    <p className="text-xs text-slate-500">Override organization defaults for this agent</p>
                  </div>
                  <button
                    onClick={() => onConfigUpdate({ 
                      use_custom_ai_model: !agentConfig.use_custom_ai_model 
                    })}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      agentConfig.use_custom_ai_model 
                        ? 'bg-indigo-500' 
                        : 'bg-white/10'
                    }`}
                    data-testid="ai-model-override-toggle"
                  >
                    <span 
                      className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        agentConfig.use_custom_ai_model ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* Configuration Form - shown when toggle is on */}
                {agentConfig.use_custom_ai_model && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4 pt-2"
                  >
                    {/* Provider Selection */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-1.5">Provider</label>
                      <select
                        value={agentConfig.custom_ai_config?.provider || ''}
                        onChange={(e) => onConfigUpdate({
                          custom_ai_config: {
                            ...agentConfig.custom_ai_config,
                            provider: e.target.value,
                            model: '' // Reset model when provider changes
                          }
                        })}
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                          text-sm text-white focus:outline-none focus:border-indigo-500/50"
                        data-testid="ai-provider-select"
                      >
                        <option value="">Select Provider</option>
                        {aiProviders.map(provider => (
                          <option key={provider.id} value={provider.id}>
                            {provider.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Model Selection - shown after provider selected */}
                    {agentConfig.custom_ai_config?.provider && (
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5">Model</label>
                        <select
                          value={agentConfig.custom_ai_config?.model || ''}
                          onChange={(e) => onConfigUpdate({
                            custom_ai_config: {
                              ...agentConfig.custom_ai_config,
                              model: e.target.value
                            }
                          })}
                          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                            text-sm text-white focus:outline-none focus:border-indigo-500/50"
                          data-testid="ai-model-select"
                        >
                          <option value="">Select Model</option>
                          {getModelsForProvider(agentConfig.custom_ai_config.provider).map(model => (
                            <option key={model.id} value={model.id}>
                              {model.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* API Key Input */}
                    {agentConfig.custom_ai_config?.provider && (
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5">API Key</label>
                        <div className="relative">
                          <input
                            type={showApiKey ? 'text' : 'password'}
                            value={agentConfig.custom_ai_config?.api_key || ''}
                            onChange={(e) => onConfigUpdate({
                              custom_ai_config: {
                                ...agentConfig.custom_ai_config,
                                api_key: e.target.value
                              }
                            })}
                            className="w-full px-3 py-2 pr-10 rounded-lg bg-white/5 border border-white/10 
                              text-sm text-white font-mono focus:outline-none focus:border-indigo-500/50"
                            placeholder="sk-..."
                            data-testid="ai-api-key-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKey(!showApiKey)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 
                              hover:text-white transition-colors"
                          >
                            {showApiKey ? <EyeSlash size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">
                          Your API key is encrypted and stored securely
                        </p>
                      </div>
                    )}

                    {/* Info Banner */}
                    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                      <Info size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-indigo-300">
                        When enabled, this agent will use your specified model and API key instead of the organization defaults.
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Section 7: Agent-Scoped Automations */}
      <div className="border-b border-white/10">
        <SectionHeader 
          id="automations" 
          title="Automations" 
          icon={Bell}
          count={automations.length}
          badge="Agent-Scoped"
        />
        <AnimatePresence>
          {expandedSections.includes('automations') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                {!agentId ? (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-xs text-amber-300">
                      Save the agent first to configure automations
                    </p>
                  </div>
                ) : loadingAutomations ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Existing Automations */}
                    {automations.length > 0 ? (
                      <div className="space-y-2">
                        {automations.map(auto => (
                          <div 
                            key={auto.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-white font-medium">{auto.name}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                  auto.is_active 
                                    ? 'bg-emerald-500/20 text-emerald-400' 
                                    : 'bg-slate-500/20 text-slate-400'
                                }`}>
                                  {auto.is_active ? 'Active' : 'Paused'}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {auto.trigger} → {auto.action_type}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleToggleAutomation(auto)}
                                className="p-1.5 text-slate-400 hover:text-white transition-colors"
                                title={auto.is_active ? 'Pause' : 'Activate'}
                              >
                                {auto.is_active ? <Pause size={14} /> : <Play size={14} />}
                              </button>
                              <button
                                onClick={() => handleDeleteAutomation(auto.id)}
                                className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
                                title="Delete"
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 p-2">No automations configured for this agent</p>
                    )}

                    {/* Add Automation */}
                    {showAddAutomation ? (
                      <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-3">
                        <input
                          type="text"
                          placeholder="Automation name"
                          value={newAutomation.name}
                          onChange={(e) => setNewAutomation({ ...newAutomation, name: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                            text-sm text-white focus:outline-none focus:border-indigo-500/50"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[10px] text-slate-500 mb-1">Trigger</label>
                            <select
                              value={newAutomation.trigger}
                              onChange={(e) => setNewAutomation({ ...newAutomation, trigger: e.target.value })}
                              className="w-full px-2 py-1.5 rounded bg-white/5 border border-white/10 
                                text-xs text-white focus:outline-none"
                            >
                              {automationTriggers.map(t => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] text-slate-500 mb-1">Action</label>
                            <select
                              value={newAutomation.action_type}
                              onChange={(e) => setNewAutomation({ ...newAutomation, action_type: e.target.value })}
                              className="w-full px-2 py-1.5 rounded bg-white/5 border border-white/10 
                                text-xs text-white focus:outline-none"
                            >
                              {automationActions.map(a => (
                                <option key={a.id} value={a.id}>{a.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setShowAddAutomation(false)}
                            className="px-3 py-1.5 text-xs text-slate-400 hover:text-white"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleAddAutomation}
                            className="px-3 py-1.5 bg-indigo-500 text-white text-xs rounded-lg hover:bg-indigo-600"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowAddAutomation(true)}
                        className="w-full flex items-center justify-center gap-2 p-2.5 rounded-lg 
                          border border-dashed border-white/20 text-xs text-slate-400 
                          hover:border-indigo-500/40 hover:text-indigo-400 transition-colors"
                      >
                        <Plus size={14} />
                        Add Automation
                      </button>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Section 7: Security & Defaults */}
      <div className="border-b border-white/10">
        <SectionHeader id="security" title="Security & Defaults" icon={Lock} />
        <AnimatePresence>
          {expandedSections.includes('security') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                {/* Side Effects Control */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Allow Side Effects</p>
                    <p className="text-xs text-slate-500">
                      Permit actions that modify external systems (emails, API calls, etc.)
                    </p>
                  </div>
                  <button
                    onClick={() => onConfigUpdate({
                      advanced_settings: {
                        ...agentConfig.advanced_settings,
                        allow_side_effects: !agentConfig.advanced_settings?.allow_side_effects
                      }
                    })}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      agentConfig.advanced_settings?.allow_side_effects 
                        ? 'bg-amber-500' 
                        : 'bg-white/10'
                    }`}
                    data-testid="allow-side-effects-toggle"
                  >
                    <span 
                      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                        agentConfig.advanced_settings?.allow_side_effects ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* Default Timeout */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">
                    Default Timeout (ms)
                    <span className="text-slate-600 ml-1">• Inherited by all nodes</span>
                  </label>
                  <input
                    type="number"
                    value={agentConfig.advanced_settings?.timeout || 30000}
                    onChange={(e) => onConfigUpdate({
                      advanced_settings: {
                        ...agentConfig.advanced_settings,
                        timeout: parseInt(e.target.value)
                      }
                    })}
                    min={1000}
                    max={300000}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                      text-sm text-white focus:outline-none focus:border-indigo-500/50"
                    data-testid="default-timeout-input"
                  />
                </div>

                {/* Default Retry Policy */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Default Retry Policy</label>
                  <select
                    value={agentConfig.advanced_settings?.retry_mode || 'none'}
                    onChange={(e) => onConfigUpdate({
                      advanced_settings: {
                        ...agentConfig.advanced_settings,
                        retry_mode: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                      text-sm text-white focus:outline-none focus:border-indigo-500/50"
                    data-testid="default-retry-select"
                  >
                    <option value="none">No Retry</option>
                    <option value="fixed">Fixed Delay Retry</option>
                    <option value="exponential">Exponential Backoff</option>
                  </select>
                </div>

                {agentConfig.advanced_settings?.retry_mode && 
                 agentConfig.advanced_settings.retry_mode !== 'none' && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5">Max Retries</label>
                    <input
                      type="number"
                      value={agentConfig.advanced_settings?.max_retries || 3}
                      onChange={(e) => onConfigUpdate({
                        advanced_settings: {
                          ...agentConfig.advanced_settings,
                          max_retries: parseInt(e.target.value)
                        }
                      })}
                      min={1}
                      max={10}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                        text-sm text-white focus:outline-none focus:border-indigo-500/50"
                    />
                  </div>
                )}

                {/* Info Banner */}
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <Info size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-300">
                    These are agent-wide defaults. Individual nodes can override these settings in their Advanced tab.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Section 8: Output Schema (UOS v2.0) */}
      <div id="config-section-output" className="border-b border-white/10 transition-all duration-300">
        <SectionHeader 
          id="output-schema" 
          title="Output Schema" 
          icon={FileText}
          badge={agentConfig.output_schema?.enabled ? "UOS v2.0" : null}
        />
        <AnimatePresence>
          {expandedSections.includes('output-schema') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                {/* Enable UOS Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Enable Structured Output</p>
                    <p className="text-xs text-slate-500">
                      Format agent output using Universal Output Schema (UOS) for professional exports
                    </p>
                  </div>
                  <button
                    onClick={() => onConfigUpdate({
                      output_schema: {
                        ...agentConfig.output_schema,
                        enabled: !agentConfig.output_schema?.enabled
                      }
                    })}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      agentConfig.output_schema?.enabled 
                        ? 'bg-indigo-500' 
                        : 'bg-white/10'
                    }`}
                    data-testid="uos-enable-toggle"
                  >
                    <span 
                      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                        agentConfig.output_schema?.enabled ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>

                {agentConfig.output_schema?.enabled && (
                  <>
                    {/* Output Type */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-1.5">Output Type</label>
                      <select
                        value={agentConfig.output_schema?.type || 'report'}
                        onChange={(e) => onConfigUpdate({
                          output_schema: {
                            ...agentConfig.output_schema,
                            type: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                          text-sm text-white focus:outline-none focus:border-indigo-500/50"
                        data-testid="uos-type-select"
                      >
                        <option value="report">📄 Report - Comprehensive analysis</option>
                        <option value="scorecard">📊 Scorecard - Evaluation with metrics</option>
                        <option value="analysis">🔍 Analysis - Data insights</option>
                        <option value="summary">📝 Summary - Condensed overview</option>
                        <option value="extraction">📋 Extraction - Data extraction results</option>
                        <option value="recommendation">💡 Recommendation - Actions & suggestions</option>
                        <option value="transcript">🎤 Transcript - Transcription output</option>
                      </select>
                    </div>

                    {/* Include Overall Score */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white">Include Overall Score</p>
                        <p className="text-xs text-slate-500">
                          Add a primary score/rating to the output summary
                        </p>
                      </div>
                      <button
                        onClick={() => onConfigUpdate({
                          output_schema: {
                            ...agentConfig.output_schema,
                            include_score: !agentConfig.output_schema?.include_score
                          }
                        })}
                        className={`relative w-10 h-5 rounded-full transition-colors ${
                          agentConfig.output_schema?.include_score 
                            ? 'bg-indigo-500' 
                            : 'bg-white/10'
                        }`}
                        data-testid="uos-score-toggle"
                      >
                        <span 
                          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                            agentConfig.output_schema?.include_score ? 'translate-x-5' : ''
                          }`}
                        />
                      </button>
                    </div>

                    {agentConfig.output_schema?.include_score && (
                      <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-indigo-500/30">
                        <div>
                          <label className="block text-xs text-slate-400 mb-1.5">Score Label</label>
                          <input
                            type="text"
                            value={agentConfig.output_schema?.score_label || 'Overall Score'}
                            onChange={(e) => onConfigUpdate({
                              output_schema: {
                                ...agentConfig.output_schema,
                                score_label: e.target.value
                              }
                            })}
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                              text-sm text-white focus:outline-none focus:border-indigo-500/50"
                            placeholder="Overall Score"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1.5">Max Score</label>
                          <input
                            type="number"
                            value={agentConfig.output_schema?.score_max || 100}
                            onChange={(e) => onConfigUpdate({
                              output_schema: {
                                ...agentConfig.output_schema,
                                score_max: parseInt(e.target.value) || 100
                              }
                            })}
                            min={1}
                            max={1000}
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                              text-sm text-white focus:outline-none focus:border-indigo-500/50"
                          />
                        </div>
                      </div>
                    )}

                    {/* Section Types */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">Expected Sections</label>
                      <p className="text-xs text-slate-500 mb-3">
                        Select which section types to include in the output. The AI will structure its response accordingly.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { id: 'scorecard', label: 'Scorecard', icon: '📊', desc: 'Ratings & metrics' },
                          { id: 'prose', label: 'Analysis', icon: '📝', desc: 'Detailed findings' },
                          { id: 'checklist', label: 'Actions', icon: '✅', desc: 'Recommendations' },
                          { id: 'table', label: 'Tables', icon: '📋', desc: 'Data tables' },
                          { id: 'key_value', label: 'Details', icon: '📌', desc: 'Key-value pairs' },
                          { id: 'transcript', label: 'Transcript', icon: '🎤', desc: 'Speaker text' },
                          { id: 'timeline', label: 'Timeline', icon: '⏱️', desc: 'Events sequence' },
                          { id: 'alert', label: 'Alerts', icon: '⚠️', desc: 'Important notices' }
                        ].map(section => {
                          const isSelected = (agentConfig.output_schema?.sections || []).includes(section.id);
                          return (
                            <button
                              key={section.id}
                              onClick={() => {
                                const currentSections = agentConfig.output_schema?.sections || [];
                                const newSections = isSelected
                                  ? currentSections.filter(s => s !== section.id)
                                  : [...currentSections, section.id];
                                onConfigUpdate({
                                  output_schema: {
                                    ...agentConfig.output_schema,
                                    sections: newSections
                                  }
                                });
                              }}
                              className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all text-left ${
                                isSelected
                                  ? 'border-indigo-500/50 bg-indigo-500/10'
                                  : 'border-white/10 bg-white/5 hover:border-white/20'
                              }`}
                              data-testid={`uos-section-${section.id}`}
                            >
                              <span className="text-lg">{section.icon}</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-white truncate">{section.label}</p>
                                <p className="text-xs text-slate-500 truncate">{section.desc}</p>
                              </div>
                              {isSelected && (
                                <CheckCircle size={14} className="text-indigo-400 flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Export Formats */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-2">Export Formats</label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { id: 'pdf', label: 'PDF', icon: '📄' },
                          { id: 'docx', label: 'Word', icon: '📝' },
                          { id: 'xlsx', label: 'Excel', icon: '📊' },
                          { id: 'markdown', label: 'Markdown', icon: '📋' }
                        ].map(format => {
                          const isSelected = (agentConfig.output_schema?.export_formats || ['pdf', 'docx']).includes(format.id);
                          return (
                            <button
                              key={format.id}
                              onClick={() => {
                                const currentFormats = agentConfig.output_schema?.export_formats || ['pdf', 'docx'];
                                const newFormats = isSelected
                                  ? currentFormats.filter(f => f !== format.id)
                                  : [...currentFormats, format.id];
                                onConfigUpdate({
                                  output_schema: {
                                    ...agentConfig.output_schema,
                                    export_formats: newFormats
                                  }
                                });
                              }}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition-all ${
                                isSelected
                                  ? 'border-indigo-500/50 bg-indigo-500/10 text-white'
                                  : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20'
                              }`}
                            >
                              <span>{format.icon}</span>
                              <span>{format.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Info Banner */}
                    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                      <Info size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-indigo-300">
                        UOS enables professional exports (PDF, Word, Excel) and consistent output rendering. 
                        The AI will structure its response based on your schema configuration.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Section 9: Advanced Settings (Collapsed by Default) - Contains System Prompt */}
      <div id="config-section-advanced" className="border-b border-white/10 transition-all duration-300">
        <SectionHeader id="advanced" title="Advanced Settings" icon={Code} />
        <AnimatePresence>
          {expandedSections.includes('advanced') && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                {/* Warning Banner */}
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <Warning size={14} className="text-amber-400 mt-0.5" />
                  <p className="text-xs text-amber-300">
                    Advanced settings affect agent behavior and execution safety.
                  </p>
                </div>

                {/* System Prompt Override */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">System Prompt Override</label>
                  <textarea
                    value={agentConfig.system_prompt || ''}
                    onChange={(e) => onConfigUpdate({ system_prompt: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                      text-sm text-white font-mono focus:outline-none focus:border-indigo-500/50 resize-none"
                    placeholder="You are a helpful assistant..."
                    data-testid="system-prompt-input"
                  />
                </div>

                {/* Execution Constraints */}
                <div>
                  <label className="block text-xs text-slate-400 mb-2">Execution Constraints</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agentConfig.advanced_settings?.allow_side_effects || false}
                        onChange={(e) => onConfigUpdate({
                          advanced_settings: {
                            ...agentConfig.advanced_settings,
                            allow_side_effects: e.target.checked
                          }
                        })}
                        className="w-4 h-4 rounded border-white/20 bg-white/5 text-indigo-500"
                      />
                      <span className="text-xs text-slate-300">Allow side effects</span>
                    </label>
                  </div>
                </div>

                {/* Timeout */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Execution Timeout (seconds)</label>
                  <input
                    type="number"
                    value={agentConfig.advanced_settings?.timeout || 30}
                    onChange={(e) => onConfigUpdate({
                      advanced_settings: {
                        ...agentConfig.advanced_settings,
                        timeout: parseInt(e.target.value)
                      }
                    })}
                    min={5}
                    max={300}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                      text-sm text-white focus:outline-none focus:border-indigo-500/50"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation Warning Modal */}
      <AnimatePresence>
        {navigationWarning.show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setNavigationWarning({ show: false, targetUrl: '', targetLabel: '' })}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md mx-4 p-6 rounded-2xl bg-[#1a1b1f] border border-white/10 shadow-2xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Warning className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Unsaved Changes</h3>
              </div>
              
              <p className="text-sm text-slate-400 mb-6">
                You have unsaved changes that will be lost if you leave this page. Would you like to save your work first?
              </p>
              
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleNavigationConfirm(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                    bg-indigo-500 hover:bg-indigo-600 text-white font-medium transition-colors"
                >
                  <Database size={16} />
                  Save Draft & Open {navigationWarning.targetLabel}
                </button>
                
                <button
                  onClick={() => handleNavigationConfirm(false)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                    bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 font-medium transition-colors"
                >
                  <ArrowSquareOut size={16} />
                  Open Without Saving
                </button>
                
                <button
                  onClick={() => setNavigationWarning({ show: false, targetUrl: '', targetLabel: '' })}
                  className="w-full px-4 py-2.5 rounded-lg text-slate-500 hover:text-white 
                    hover:bg-white/5 font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConfigPanel;
